/** UNIVERSAL TIME SERIES — any number's history, for the series modal. */
import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import {
  PORTFOLIO_VALUE_HISTORY, PORTFOLIO_RISK_HISTORY, MF_PRICE_HISTORY,
  HOLDING_WEIGHTS_HISTORY, FUND_HOLDINGS_HISTORY, FACTOR_EXPOSURES_HISTORY,
  MF_FACTOR_HISTORY, PORTFOLIO_RISK_TIME_SERIES, VALUATION_HISTORY,
  CLASSIFICATION_HISTORY, STOCK_PRICE_HISTORY, MF_TRANSACTIONS_HISTORY,
  STOCK_FUNDAMENTAL_FIELDS, stockFundamentalHistory,
} from '../queries';
import { STYLE_FACTOR_EXPOSURES, STYLE_RISK_HISTORY } from '../queries/styleFactors';
import { useBook } from './book';
import { STANDARD_VAR, isBaseScenario } from '../../lib/portfolio';
import { tradeMarker } from '../../lib/trading';
import { EMPTY } from './_shared';

/**
 * The risk family, metric by metric.
 *
 * `measured` is the historical pass and `forecast` the simulation; whichever
 * carries a number answers, measured first, because a measurement beats a
 * projection. `kind` says how the stored figure relates to the one on screen —
 * a `pct` metric is a fraction and is scaled, a `num` metric is a ratio or a
 * score and is plotted as stored.
 *
 * Spelled out rather than pattern-matched. Matching on `includes()` cannot
 * separate `var99` and `cvar95` from `var95` — all three contain "var" — and
 * every name it failed to match fell through to a volatility default, silently.
 * Alpha, sortino, the expected return and the risk score each drew the
 * volatility line under their own heading, and the two CVaRs drew VaR 95's.
 * An unknown key now reports itself unsupported instead.
 */
const RISK_METRICS = {
  expectedreturn: { measured: 'realizedExpectedReturn', forecast: 'forecastExpectedReturn', kind: 'pct' },
  benchmarkreturn: { measured: 'realizedBenchmarkReturn', forecast: 'forecastBenchmarkReturn', kind: 'pct' },
  volatility: { measured: 'realizedVolatility', forecast: 'forecastVolatility', kind: 'pct' },
  benchmarkvolatility: { measured: 'benchmarkVolatility', kind: 'pct' },
  var95: { measured: 'realizedVar95', forecast: 'forecastVar95', kind: 'pct' },
  var99: { measured: 'realizedVar99', forecast: 'forecastVar99', kind: 'pct' },
  cvar95: { measured: 'realizedCvar95', forecast: 'forecastCvar95', kind: 'pct' },
  cvar99: { measured: 'realizedCvar99', forecast: 'forecastCvar99', kind: 'pct' },
  alpha: { measured: 'realizedAlpha', forecast: 'forecastAlpha', kind: 'pct' },
  trackingerror: { measured: 'realizedTrackingError', forecast: 'forecastTrackingError', kind: 'pct' },
  maxdrawdown: { measured: 'realizedMaxDrawdown', kind: 'pct' },
  /* The only metric that exists solely on the decomposition rows — a scenario
     as a whole is not held at a weight. */
  weight: { measured: 'weight', kind: 'pct', holdingOnly: true },
  sharperatio: { measured: 'realizedSharpeRatio', forecast: 'forecastSharpeRatio', kind: 'num' },
  sortinoratio: { measured: 'realizedSortinoRatio', forecast: 'forecastSortinoRatio', kind: 'num' },
  informationratio: { measured: 'forecastInformationRatio', kind: 'num' },
  betatobenchmark: { measured: 'realizedBetaToBenchmark', forecast: 'forecastBetaToBenchmark', kind: 'num' },
  correlationtobenchmark: { measured: 'forecastCorrelationToBenchmark', kind: 'num' },
  riskvsbenchmark: { measured: 'riskVsBenchmark', kind: 'num' },
  riskscore: { measured: 'riskScore', kind: 'num' },
};

/** Short names the tables use for the same metrics. */
const RISK_ALIAS = {
  te: 'trackingerror',
  beta: 'betatobenchmark',
  sharpe: 'sharperatio',
  sortino: 'sortinoratio',
  ir: 'informationratio',
  correlation: 'correlationtobenchmark',
  drawdown: 'maxdrawdown',
  score: 'riskscore',
};

/**
 * Which of those fields a per-holding row actually carries.
 *
 * A drawdown, a realised ratio and the risk score are properties of the whole
 * subject, so the decomposition rows have no column for them — the same reason
 * those cells are blank in the risk table.
 */
const HOLDING_RISK_FIELDS = new Set([
  'weight',
  'realizedExpectedReturn', 'realizedBenchmarkReturn', 'realizedVolatility',
  'realizedVar95', 'realizedVar99', 'realizedCvar95', 'realizedCvar99',
  'realizedAlpha', 'realizedBetaToBenchmark', 'realizedTrackingError',
  'forecastExpectedReturn', 'forecastBenchmarkReturn', 'forecastVolatility',
  'forecastVar95', 'forecastVar99', 'forecastCvar95', 'forecastCvar99',
  'forecastAlpha', 'forecastSharpeRatio', 'forecastSortinoRatio',
  'forecastInformationRatio', 'forecastBetaToBenchmark',
  'forecastCorrelationToBenchmark', 'forecastTrackingError',
]);

/** `risk.te`, `risk.trackingError` and `risk.tracking_error` are one metric. */
const riskMetric = (metric) => {
  const name = String(metric || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  return RISK_METRICS[name] ?? RISK_METRICS[RISK_ALIAS[name]] ?? null;
};

/**
 * The three metrics `riskTimeSeries` publishes, by the same names the risk
 * family uses.
 *
 * It answers the same questions as the scenario history but pre-paired daily,
 * so it stands in when the scenario rows have nothing for one of the three. It
 * is a fallback rather than the primary source because the scenario history is
 * scenario-aware and this is not: one series, no stress runs.
 */
const RISK_SERIES_FIELDS = {
  volatility: 'portfolioVolatility',
  benchmarkvolatility: 'benchmarkVolatility',
  trackingerror: 'trackingError',
};

const riskSeriesField = (metric) => {
  const name = String(metric || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  return RISK_SERIES_FIELDS[name] ?? RISK_SERIES_FIELDS[RISK_ALIAS[name]] ?? null;
};

/**
 * A factor loading, by the name the exposure bars key on.
 *
 * `fundFactor.*` and `factor.*` ask the same question of the two kinds of
 * subject and stay separate keys for the same reason `holding.*` and
 * `fundHolding.*` do: only the caller knows whether it is looking at a scheme
 * or a portfolio, and neither endpoint holds anything for the other. The two
 * histories answer with the same row type, so this map serves both.
 *
 * `momentum` is mapped and always null on this backend — the two dated momentum
 * scores carry it — so a chart for it says there is no history rather than
 * quietly drawing one of the others.
 */
const FUND_FACTOR_FIELDS = {
  beta: 'beta',
  alpha: 'alphaScore',
  alphascore: 'alphaScore',
  rsquared: 'rSquared',
  momentum: 'momentumScore',
  stmomentum: 'shortTermMomentumScore',
  shorttermmomentum: 'shortTermMomentumScore',
  ltmomentum: 'longTermMomentumScore',
  longtermmomentum: 'longTermMomentumScore',
  value: 'valueScore',
  quality: 'qualityScore',
  size: 'sizeScore',
  growth: 'growthScore',
  highvolatility: 'highVolatilityScore',
  lowvolatility: 'lowVolatilityScore',
};

const fundFactorField = (metric) =>
  FUND_FACTOR_FIELDS[String(metric || '').toLowerCase().replace(/[^a-z0-9]/g, '')] ?? null;

/**
 * One holding's weight off a factor-history row, as a percentage — the series
 * behind the weight chart and behind the weight laid over a stock's price. A
 * fraction on the row, a percentage on screen: the same trap every other
 * weight source has, resolved in one place. Null where the row does not carry
 * the holding, which is a date the fund did not hold it or the pass did not
 * write, and either way not a zero.
 */
const holdingWeight = (row, holding) => {
  const c = (row?.holdingFactorContributions ?? EMPTY).find((x) => x.holdingSecuritySymbol === holding);
  return c?.weight == null ? null : Number(c.weight) * 100;
};

/**
 * The factors whose per-holding contributions the documents select — every
 * loading except the always-null combined momentum. A holding question about
 * that one is unsupported rather than answered with an empty series, the same
 * contract `HOLDING_RISK_FIELDS` keeps for the risk family.
 */
const CONTRIBUTED_FACTOR_FIELDS = new Set([
  'beta', 'alphaScore', 'rSquared',
  'shortTermMomentumScore', 'longTermMomentumScore', 'valueScore',
  'qualityScore', 'sizeScore', 'growthScore',
  'highVolatilityScore', 'lowVolatilityScore',
]);

/** The only three windows the fund factor endpoint understands. */
const factorFilter = (months) => (months >= 60 ? '5y' : months >= 36 ? '3y' : '1y');

/**
 * The modal's four windows in the vocabulary the price and transactions
 * histories share. `all` is real here, unlike the factor endpoint, so Max
 * reaches back to the first stored row.
 */
const priceFilter = (months) => (months >= 240 ? 'all' : months >= 60 ? '5y' : months >= 36 ? '3y' : '1y');

/**
 * A stock's valuation multiples, by the name the valuation table keys on.
 *
 * No scaling anywhere, unlike the risk family: a multiple is a ratio, and the
 * two rates here are stored as percentages already — a dividend yield arrives
 * as 0.69 meaning 0.69%, which is exactly how the table prints it. Every value
 * plots as stored.
 *
 * `benchmarkWeight` is absent on purpose — see VALUATION_HISTORY for why.
 */
const VALUATION_FIELDS = {
  marketcap: 'marketCap',
  pe: 'peRatio',
  peratio: 'peRatio',
  forwardpe: 'forwardPe',
  fwdpe: 'forwardPe',
  peg: 'pegRatio',
  pegratio: 'pegRatio',
  pb: 'priceBookMrq',
  pricebook: 'priceBookMrq',
  ps: 'priceSalesTtm',
  pricesales: 'priceSalesTtm',
  evebitda: 'enterpriseValueEbitda',
  dividendyield: 'dividendYield',
  divyield: 'dividendYield',
  roe: 'returnOnEquityTtm',
};

const valuationField = (metric) =>
  VALUATION_FIELDS[String(metric || '').toLowerCase().replace(/[^a-z0-9]/g, '')] ?? null;

/**
 * The classification axes, by the id the exposure panel keys them on — which
 * is also the field name on the response, so one name serves the include flag
 * and the read.
 */
const CLASSIFICATION_AXES = [
  'sectors', 'industries', 'assetClasses', 'securityTypes',
  'countries', 'regions', 'currencies',
];

const classificationAxis = (metric) => {
  const name = String(metric || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  return CLASSIFICATION_AXES.find((a) => a.toLowerCase() === name) ?? null;
};

/**
 * Resolves a metric key to a real backend series.
 * Supported today: portfolio market value / daily return (portfolio.*),
 * realised risk metrics (risk.*) and fund NAV / AUM (fund.*). Anything else
 * returns `supported: false` and the modal says so rather than inventing a line.
 */
/**
 * Resolve a metric key to a real backend series.
 *
 * Keys are `family.metric`, optionally with the holding a row belongs to as the
 * entity. The families that have a published history:
 *
 *   portfolio.*  market value, daily return          getPortfolioValueHistory
 *   risk.*       volatility, VaR, Sharpe, drawdown,  getPortfolioRiskMetricsHistory
 *                beta
 *   fund.*       NAV, AUM                            getMutualFundPriceHistory
 *   holding.*    weight, value, price, P&L           getPortfolioHoldingWeightsHistory
 *   fundHolding.* one disclosed position's weight    getMetaMutualFund.holdingsHistory
 *                or value
 *   factor.*     a portfolio's factor exposure       getPortfolioFactorExposuresHistory
 *   fundFactor.* a scheme's factor loading           getMutualFundFactorExposuresHistory
 *   valuation.*  one stock's multiples              getValuationHistory
 *   fundamental.* any one figure of a stock's       getStockFundamentalsHistory
 *                dated fundamentals record
 *   classification.* a bucket's weight in the       getClassificationHistory
 *                subject, by axis
 *   stock.*      one listed stock's price, with a   getStockPriceHistory
 *                fund's trades in it marked         + getMutualFundTransactionsHistory
 *
 * `stock.*` is keyed on the stock like `valuation.*` is. `fund` names the
 * scheme whose trades to mark on the series — on `stock.price`, or on a
 * `fundFactor.weight` scoped to a holding — and the markers come back from
 * this hook rather than from the caller because they have to cover the window
 * being charted: the transactions table holds a year of filings and the chart
 * offers five, so a caller's own list left the older dots off one chart and
 * not the other. The filings are asked for over the same window as the line.
 *
 * `classification.*` names the AXIS in the metric and the bucket in `holding`:
 * `classification.sectors` scoped to "Financial Services" is that sector's
 * weight in the subject over time. The bucket rides in the `holding` slot
 * because that is the generic "narrow to one row" channel, even though the row
 * here is a sector rather than a security.
 *
 * `valuation.*` is the exception to the rule that `entity` is the subject: that
 * endpoint is keyed on the security being valued and answers empty for a fund
 * or a portfolio, so its caller passes the stock's own symbol rather than the
 * fund holding it, and leaves `holding` unset.
 *
 * `holding.*` and `fundHolding.*` answer the same question for the two kinds of
 * subject, and stay separate keys because only the caller knows which it is: a
 * scheme's positions are its disclosures, a portfolio's are its own rows, and
 * neither table holds anything for the other's symbols.
 *
 * A `holding` on the two factor families charts that holding's CONTRIBUTION to
 * the factor — its weight times its own loading, the figure the drill-down row
 * shows — not the holding's loading, which neither history stores.
 *
 * `scenario` scopes a `risk.*` series to one run, by the `scenarioValue` the
 * risk table is keyed on. Risk history holds a row per date PER SCENARIO, so a
 * figure clicked in a stress row and the same figure clicked in the base row
 * are two different series; without the scope both charted whatever the
 * response listed first for each date, which is one arbitrary scenario for all
 * of them.
 *
 * Anything else reports `supported: false`, and the modal says so rather than
 * drawing a line that does not exist.
 */
export function useMetricSeries({ metricKey, entity, holding, scenario, fund, months = 36 }) {
  const key = String(metricKey || '');
  /* The FIRST dot only: a family's metric may itself be dotted, as the style
     risk family's `<kind>.<measure>` is. Destructuring `split('.')` silently
     dropped everything past the second segment. */
  const dot = key.indexOf('.');
  const family = dot === -1 ? key : key.slice(0, dot);
  const metric = dot === -1 ? '' : key.slice(dot + 1);
  const from = new Date();
  from.setMonth(from.getMonth() - months);
  const fromDate = from.toISOString().slice(0, 10);

  const value = useQuery(PORTFOLIO_VALUE_HISTORY, {
    variables: { securitySymbol: entity, fromDate, toDate: null },
    skip: family !== 'portfolio' || !entity,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  /**
   * A question about the base case never reads a stress replay, and on this
   * data those are 520 of the 556 rows. Filtered server-side, the response with
   * per-holding rows drops from about 1.9 MB to 96 KB.
   *
   * Only when the question IS about the unstressed run — unscoped, or scoped to
   * `standard_var`. A stress scenario's own figure needs the rows the filter
   * would remove, so it goes unfiltered.
   */
  const baseCaseOnly = !scenario || String(scenario).trim().toLowerCase() === STANDARD_VAR;
  const risk = useQuery(PORTFOLIO_RISK_HISTORY, {
    variables: {
      securitySymbol: entity,
      fromDate,
      toDate: null,
      riskType: baseCaseOnly ? 'RISK' : null,
      withHoldings: Boolean(holding),
    },
    skip: family !== 'risk' || !entity,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  const nav = useQuery(MF_PRICE_HISTORY, {
    variables: { securitySymbol: entity, filter: months >= 60 ? '5y' : months >= 36 ? '3y' : '1y' },
    skip: family !== 'fund' || !entity,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  const weights = useQuery(HOLDING_WEIGHTS_HISTORY, {
    variables: { securitySymbol: entity, fromDate, toDate: null },
    skip: family !== 'holding' || !entity,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  /* No date range to pass — the field takes none and answers with the whole
     disclosure history, which is monthly and therefore short. */
  const disclosures = useQuery(FUND_HOLDINGS_HISTORY, {
    variables: { securitySymbol: entity },
    skip: family !== 'fundHolding' || !entity,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  /* Book-level roll-ups have a real series: every portfolio's daily snapshot,
     summed by date. Counts do not — nothing stores "how many clients were off
     target last Tuesday". */
  const book = useBook({ withHistory: family === 'book' });

  /* Contribution rows multiply every date by the holding count — 230 KB on
     top of 84 on the fund sampled — so both factor histories fetch them only
     when the question is about one holding's pull. */
  const factors = useQuery(FACTOR_EXPOSURES_HISTORY, {
    variables: { securitySymbol: entity, fromDate, toDate: null, withHoldings: Boolean(holding) },
    skip: family !== 'factor' || !entity,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  /* A scheme's own factor history — see FUND_FACTOR_FIELDS for why this is a
     separate family rather than a fallback inside `factor`. */
  const fundFactors = useQuery(MF_FACTOR_HISTORY, {
    variables: { securitySymbol: entity, filter: factorFilter(months), withHoldings: Boolean(holding) },
    skip: family !== 'fundFactor' || !entity,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  /* One axis per request — see CLASSIFICATION_HISTORY for the difference that
     makes. The flag for the axis being charted is the only one set. */
  const axis = family === 'classification' ? classificationAxis(metric) : null;
  const classifications = useQuery(CLASSIFICATION_HISTORY, {
    variables: {
      securitySymbol: entity,
      fromDate,
      toDate: null,
      ...Object.fromEntries(CLASSIFICATION_AXES.map((a) => [a, a === axis])),
    },
    skip: family !== 'classification' || !entity || !axis,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  /* The stock's dated fundamentals record, one field of it. Keyed on the stock
     like `valuation.*`; the metric IS the field's name. A field the record
     does not carry as a number is refused below rather than asked for. The
     endpoint takes a row count, not dates: about a trading month's rows per
     month asked for, newest first. */
  const fundamentalField = family === 'fundamental' && STOCK_FUNDAMENTAL_FIELDS.has(metric) ? metric : null;
  const fundamentals = useQuery(stockFundamentalHistory(fundamentalField || 'peRatio'), {
    variables: { securitySymbol: entity, limit: Math.min(1500, Math.max(30, months * 23)) },
    skip: !fundamentalField || !entity,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  /* Keyed on the stock itself rather than on a subject holding it, so `entity`
     here is the security being valued — the one family where that is true. */
  const valuations = useQuery(VALUATION_HISTORY, {
    variables: { securitySymbol: entity, fromDate, toDate: null },
    skip: family !== 'valuation' || !entity,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  /* The stock's own closes. */
  const prices = useQuery(STOCK_PRICE_HISTORY, {
    variables: { securitySymbol: entity, filter: priceFilter(months) },
    skip: family !== 'stock' || !entity,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  /**
   * A fundamental style exposure through time — the `style.<factor>` family.
   *
   * The factor name is the metric, so `style.short_term_momentum` charts that
   * z-score for whatever the row belongs to. Underscores are safe in the key
   * because it is split on the first dot only.
   */
  const style = useQuery(STYLE_FACTOR_EXPOSURES, {
    variables: { securitySymbol: entity, startDate: fromDate, endDate: null },
    skip: family !== 'style' || !entity,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  /**
   * The style model's RISK through time — `stylerisk.<kind>.<measure>`.
   *
   * Two segments after the family because the same measure exists twice: the
   * standalone volatility and the tracking error are different series off the
   * same endpoint, chosen by `riskKind`. `stylerisk.active.totalRisk` is the
   * tracking error; `stylerisk.total.specificRisk` the stock-specific part of
   * the standalone risk.
   *
   * Deliberately NOT the `risk` family: that one reads the portfolio risk
   * snapshots, which are a different model with different numbers. A figure
   * that opened a series from the other model would be charting something the
   * reader did not click on.
   */
  const styleRiskKind = family === 'stylerisk' ? (metric.split('.')[0] || 'total') : null;
  const styleRisk = useQuery(STYLE_RISK_HISTORY, {
    variables: { securitySymbol: entity, riskKind: styleRiskKind, startDate: fromDate, endDate: null },
    skip: family !== 'stylerisk' || !entity,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  /* The scheme's filings over the window being charted, whenever a caller
     named the fund whose trades to mark — on a stock's price, or on the
     stock's weight in that fund. One source for both charts, so the same
     window shows the same dots on either. */
  const trades = useQuery(MF_TRANSACTIONS_HISTORY, {
    variables: { securitySymbol: fund, filter: priceFilter(months) },
    skip: !fund,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  /* The fund's weight in the stock, laid over its price — the same rows the
     weight chart draws from, asked for the nearest window the factor endpoint
     has (it stops at five years where the price goes back further). */
  const stockWeights = useQuery(MF_FACTOR_HISTORY, {
    variables: { securitySymbol: fund, filter: factorFilter(months), withHoldings: true },
    skip: family !== 'stock' || !entity || !fund,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  /* Fetched only for the three metrics it publishes, and only as the scenario
     history's fallback — see RISK_SERIES_FIELDS. */
  const riskSeries = useQuery(PORTFOLIO_RISK_TIME_SERIES, {
    variables: { securitySymbol: entity, startDate: fromDate, endDate: null },
    skip: family !== 'risk' || !entity || !riskSeriesField(metric),
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  return useMemo(() => {
    const series = (rows, date, pick) =>
      rows
        .filter((r) => r?.[date] != null)
        .map((r) => ({ date: r[date], value: Number(pick(r)) || 0 }))
        .sort((a, b) => (a.date < b.date ? -1 : 1));

    /* The fund's every disclosed move in one stock, dated on its filing. The
       stock is the entity on a price chart and the holding on a weight chart;
       the filings name it by the same symbol either way, so the match is
       exact. Undefined when no fund was named, so callers' own markers stand. */
    const marked = family === 'stock' ? entity : holding;
    const filings = trades.data?.getMutualFundTransactionsHistory?.history ?? EMPTY;
    const markers = fund && marked
      ? filings.flatMap((p) => (p.changes ?? EMPTY)
        .filter((c) => c.securitySymbol === marked)
        .map((c) => tradeMarker(p, c)))
      : undefined;

    if (family === 'stylerisk') {
      const rows = styleRisk.data?.fundamentalStyleFactorRiskHistory ?? EMPTY;
      /* `<kind>.<measure>` — the measure is the second segment. */
      const field = metric.split('.')[1] || 'totalRisk';
      const points = rows
        .filter((r) => r?.asOfDate != null && r[field] != null)
        .map((r) => ({ date: r.asOfDate, value: Number(r[field]) }))
        .sort((a, b) => (a.date < b.date ? -1 : 1));
      return { supported: true, loading: styleRisk.loading, error: styleRisk.error, points };
    }
    if (family === 'style') {
      const rows = style.data?.fundamentalStyleFactorExposures ?? EMPTY;
      /* A date the model fitted but did not score this factor on is left out
         rather than plotted as zero — nothing is not the same as the market
         average, which is exactly what zero means on a z-score. */
      const points = rows
        .map((r) => ({
          date: r.asOfDate,
          value: (r.exposures ?? EMPTY).find((e) => e.factor === metric)?.exposure,
        }))
        .filter((p) => p.date != null && p.value != null)
        .map((p) => ({ date: p.date, value: Number(p.value) }))
        .sort((a, b) => (a.date < b.date ? -1 : 1));
      return { supported: true, loading: style.loading, error: style.error, points };
    }
    if (family === 'portfolio') {
      const rows = value.data?.getPortfolioValueHistory ?? EMPTY;
      const field = metric.toLowerCase().includes('return') ? 'dailyReturn' : 'marketValue';
      return {
        supported: true, loading: value.loading, error: value.error,
        points: series(rows, 'latestDate', (r) => r[field]),
      };
    }
    if (family === 'risk') {
      const spec = riskMetric(metric);
      /* A key with no field behind it is not a metric with an empty series —
         it is a name nothing publishes, and saying so beats drawing a line
         borrowed from whichever metric a loose match happened to land on. */
      if (!spec) return { supported: false, loading: false, error: null, points: EMPTY };

      /* A metric the requested level does not carry is unsupported at that
         level — better said outright than charted as a flat empty series. */
      const fields = spec.holdingOnly && !holding
        ? EMPTY
        : [spec.measured, spec.forecast].filter(Boolean).filter((f) => !holding || HOLDING_RISK_FIELDS.has(f));
      if (!fields.length) return { supported: false, loading: false, error: null, points: EMPTY };

      const rows = risk.data?.getPortfolioRiskMetricsHistory ?? EMPTY;

      /* Read off the row itself, or off the clicked holding's slice of it. */
      const read = (r) => {
        const source = holding
          ? (r.holdingRiskContributions ?? EMPTY).find((c) => c.holdingSecuritySymbol === holding)
          : r;
        if (!source) return null;
        for (const f of fields) {
          const raw = source[f];
          if (raw == null) continue;
          const v = Number(raw);
          /* Fractions on the wire, percentages on screen — but only for the
             metrics that are fractions. A Sharpe of 0.8 scaled the same way
             drew a line at 80 beside a "Current" readout of 0.80. */
          if (Number.isFinite(v)) return spec.kind === 'pct' ? v * 100 : v;
        }
        return null;
      };

      /* One point per date. Scoped to a scenario the match is exact, so a
         stress replay never quietly answers with the base run's figure; unscoped
         the base run answers, and only a date it never wrote the metric on falls
         through to another scenario. A date the metric was never computed on
         gets no point at all — read as a zero it dragged the line to the floor
         and put 0 in the high/low readout. */
      const wanted = String(scenario || '').trim().toLowerCase();
      const scoped = wanted
        ? rows.filter((r) => String(r?.scenarioValue || r?.scenarioName || '').trim().toLowerCase() === wanted)
        : [...rows].sort((a, b) => Number(isBaseScenario(b)) - Number(isBaseScenario(a)));

      const byDate = new Map();
      scoped.forEach((r) => {
        if (!r?.latestDate || byDate.has(r.latestDate)) return;
        const v = read(r);
        if (v != null) byDate.set(r.latestDate, v);
      });
      const points = [...byDate.entries()]
        .map(([date, value]) => ({ date, value }))
        .sort((a, b) => (a.date < b.date ? -1 : 1));

      /* Volatility, the benchmark's, and the tracking error are also published
         pre-paired and daily. Used only when the scenario rows produced nothing:
         that series carries no scenario, so it cannot answer a scoped question. */
      const seriesField = riskSeriesField(metric);
      if (!points.length && seriesField && !wanted) {
        const rows = riskSeries.data?.getPortfolio?.portfolio?.riskTimeSeries ?? EMPTY;
        return {
          supported: true,
          loading: risk.loading || riskSeries.loading,
          error: risk.error || riskSeries.error,
          points: rows
            .filter((r) => r?.latestDate && r[seriesField] != null)
            .map((r) => ({ date: r.latestDate, value: Number(r[seriesField]) * 100 }))
            .filter((p) => Number.isFinite(p.value))
            .sort((a, b) => (a.date < b.date ? -1 : 1)),
        };
      }

      return {
        supported: true,
        loading: risk.loading,
        error: risk.error,
        points,
      };
    }

    if (family === 'fundFactor' || family === 'factor') {
      /* `weight` is the one non-factor metric on the contribution rows — a
         holding's disclosed weight, riding along with its contributions. It is
         a row's property with no subject-level counterpart on these histories,
         so without a holding it is unsupported, like `risk.weight`. */
      const wantsWeight = String(metric).toLowerCase().replace(/[^a-z0-9]/g, '') === 'weight';
      const field = wantsWeight ? null : fundFactorField(metric);
      if (!wantsWeight && !field) return { supported: false, loading: false, error: null, points: EMPTY };
      if (wantsWeight && !holding) return { supported: false, loading: false, error: null, points: EMPTY };
      /* A holding's pull on a factor exists only for the factors the documents
         select — anything else is a name with no contribution field behind it. */
      if (holding && field && !CONTRIBUTED_FACTOR_FIELDS.has(field)) {
        return { supported: false, loading: false, error: null, points: EMPTY };
      }
      const q = family === 'fundFactor' ? fundFactors : factors;
      const rows = (family === 'fundFactor'
        ? q.data?.getMutualFundFactorExposuresHistory?.history
        : q.data?.getPortfolioFactorExposuresHistory) ?? EMPTY;
      /* The subject's own loading, or the clicked holding's slice of it. A date
         the contribution pass has not written gets no point rather than a zero
         — on this data those rows are far younger than the loadings. */
      const read = (r) => {
        if (!holding) return r[field];
        if (wantsWeight) return holdingWeight(r, holding);
        const c = (r.holdingFactorContributions ?? EMPTY)
          .find((x) => x.holdingSecuritySymbol === holding);
        return c ? c[`${field}Contribution`] : null;
      };
      return {
        supported: true,
        /* The dots wait for the line when a fund's trades were asked for. */
        loading: q.loading || (fund ? trades.loading : false),
        error: q.error,
        markers,
        /* Loadings, not percentages: a value of 0.87 is a loading of 0.87 and
           is plotted as it stands, the way the exposure bars show it. */
        points: rows
          .filter((r) => r?.asOfDate)
          .map((r) => {
            const v = read(r);
            return v == null ? null : { date: r.asOfDate, value: Number(v) };
          })
          .filter((p) => p && Number.isFinite(p.value))
          .sort((a, b) => (a.date < b.date ? -1 : 1)),
      };
    }
    if (family === 'fund') {
      const rows = nav.data?.getMutualFundPriceHistory?.fundHistory ?? EMPTY;
      const field = metric.toLowerCase() === 'aum' ? 'aum' : 'nav';
      return {
        supported: true, loading: nav.loading, error: nav.error,
        points: series(rows, 'date', (r) => r[field] ?? r.dayEndNav),
      };
    }
    if (family === 'holding') {
      const all = weights.data?.getPortfolioHoldingWeightsHistory ?? EMPTY;
      /* One row per holding per date — keep only the holding this number is for. */
      const rows = holding ? all.filter((r) => r.holdingSecuritySymbol === holding) : all;
      const m = metric.toLowerCase();
      const isWeight = m.includes('weight');
      const field =
        isWeight ? 'weight'
          : m.includes('price') ? 'price'
            : m.includes('pnl') || m.includes('gain') ? 'unrealizedPnl'
              : 'marketValue';
      return {
        supported: true, loading: weights.loading, error: weights.error,
        /* A portfolio stores weights as fractions, and the modal quotes this
           metric as a percentage — a tenth of the book read as 0.1%. */
        points: series(rows, 'latestDate', (r) => (isWeight ? r[field] * 100 : r[field])),
      };
    }
    if (family === 'fundHolding') {
      const all = disclosures.data?.getMetaMutualFund?.holdingsHistory ?? EMPTY;
      const rows = holding ? all.filter((r) => r.holdingSymbol === holding) : all;
      /* Already a percentage here, unlike the portfolio path above. */
      const field = metric.toLowerCase().includes('weight') ? 'weight' : 'marketValue';
      return {
        supported: true,
        loading: disclosures.loading,
        error: disclosures.error,
        /* A disclosure the position was absent from is not a zero weight — the
           fund did not hold it — so those dates have no point rather than one
           on the floor. */
        points: rows
          .filter((r) => r?.asOfDate && r[field] != null)
          .map((r) => ({ date: r.asOfDate, value: Number(r[field]) }))
          .filter((p) => Number.isFinite(p.value))
          .sort((a, b) => (a.date < b.date ? -1 : 1)),
      };
    }
    if (family === 'classification') {
      if (!axis) return { supported: false, loading: false, error: null, points: EMPTY };
      /* `holding` carries the bucket — the generic "narrow to this row" slot,
         which for this family is a sector or a country rather than a name. */
      if (!holding) return { supported: false, loading: false, error: null, points: EMPTY };
      const rows = classifications.data?.getClassificationHistory ?? EMPTY;
      const wanted = String(holding).trim().toLowerCase();
      return {
        supported: true,
        loading: classifications.loading,
        error: classifications.error,
        /* A date that published NO bucket on this axis is a date the pass did
           not run, and gets no point. A date that published others but not
           this one is a real zero — the subject held nothing in that bucket —
           and that is the drift worth charting. Reading the two the same way
           would draw a fall to 0% and back on the four dates a fund's
           classification run happens to be missing. */
        points: rows
          .filter((r) => r?.latestDate && (r[axis] ?? EMPTY).length)
          .map((r) => {
            const hit = (r[axis] ?? EMPTY).find(
              (b) => String(b?.classificationValue || '').trim().toLowerCase() === wanted,
            );
            return { date: r.latestDate, value: hit ? Number(hit.weight) * 100 : 0 };
          })
          .filter((p) => Number.isFinite(p.value))
          .sort((a, b) => (a.date < b.date ? -1 : 1)),
      };
    }

    if (family === 'fundamental') {
      if (!fundamentalField) return { supported: false, loading: false, error: null, points: EMPTY };
      const rows = fundamentals.data?.getStockFundamentalsHistory ?? EMPTY;
      return {
        supported: true,
        loading: fundamentals.loading,
        error: fundamentals.error,
        /* A date the figure was not stored on gets no point rather than a zero. */
        points: rows
          .filter((r) => r?.asOfDate && r[fundamentalField] != null)
          .map((r) => ({ date: r.asOfDate, value: Number(r[fundamentalField]) }))
          .filter((p) => Number.isFinite(p.value))
          .sort((a, b) => (a.date < b.date ? -1 : 1)),
      };
    }

    if (family === 'valuation') {
      const field = valuationField(metric);
      if (!field) return { supported: false, loading: false, error: null, points: EMPTY };
      const rows = valuations.data?.getValuationHistory ?? EMPTY;
      return {
        supported: true,
        loading: valuations.loading,
        error: valuations.error,
        /* A date the multiple was not published on gets no point rather than a
           zero — PEG is absent on roughly seven dates in eight, and read as
           zero it would draw a line along the floor with occasional spikes. */
        points: rows
          .filter((r) => r?.asOfDate && r[field] != null)
          .map((r) => ({ date: r.asOfDate, value: Number(r[field]) }))
          .filter((p) => Number.isFinite(p.value))
          .sort((a, b) => (a.date < b.date ? -1 : 1)),
      };
    }

    if (family === 'book') {
      return {
        supported: true,
        loading: book.loading,
        error: book.error,
        points: book.valueSeries.map((p) => ({ date: p.date, value: p.value })),
      };
    }

    if (family === 'stock') {
      if (metric.toLowerCase() !== 'price') return { supported: false, loading: false, error: null, points: EMPTY };
      const rows = prices.data?.getStockPriceHistory?.stockHistory ?? EMPTY;
      /* The position over the same stretch, so a trade is read against what
         it did to the holding: a buy dot under a rising weight line is a
         desk building a position, the same dot under a flat one is a top-up.

         Two sources, joined where one ends. The daily series is the factor
         pass's per-holding weight, which starts where that pass began writing
         — January 2025 on the fund sampled, against filings back to 2023 — so
         before its first day the weights come off the filings themselves: each
         move carries the position's weight before and after it, in the same
         units (2.04% on both at the turn of 2025), and a filed weight stands
         until the next filing. Left as the only source when the pass has not
         reached the fund at all. Only the months the stock moved are filed
         here, which is exactly the dates the dots sit on. */
      const daily = (stockWeights.data?.getMutualFundFactorExposuresHistory?.history ?? EMPTY)
        .filter((r) => r?.asOfDate)
        .map((r) => {
          const v = holdingWeight(r, entity);
          return v == null || !Number.isFinite(v) ? null : { date: r.asOfDate, value: v };
        })
        .filter(Boolean)
        .sort((a, b) => (a.date < b.date ? -1 : 1));
      const dailyFrom = daily[0]?.date ?? null;
      const filed = new Map();
      filings.forEach((p) => (p.changes ?? EMPTY)
        .filter((c) => c.securitySymbol === entity)
        .forEach((c) => {
          [[p.fromDate, c.prevWeight], [p.asOfDate, c.currWeight]].forEach(([date, w]) => {
            const v = Number(w);
            if (date && Number.isFinite(v) && (!dailyFrom || date < dailyFrom)) filed.set(date, v);
          });
        }));
      const overlay = fund
        ? {
          label: 'Weight in fund',
          type: 'pct',
          points: [
            ...[...filed.entries()].map(([date, value]) => ({ date, value, hold: true })),
            ...daily,
          ].sort((a, b) => (a.date < b.date ? -1 : 1)),
        }
        : undefined;
      return {
        supported: true,
        /* The dots and the weight wait for the line: a chart that draws first
           and grows them a moment later reads as a glitch. */
        loading: prices.loading || (fund ? trades.loading || stockWeights.loading : false),
        error: prices.error,
        /* Split-adjusted where published, so a bonus issue is not a crash. */
        points: series(rows, 'date', (r) => r.adjustedClose ?? r.close),
        markers,
        overlay,
      };
    }

    return { supported: false, loading: false, error: null, points: EMPTY };
  }, [family, metric, entity, holding, scenario, fund, axis, value, risk, riskSeries, nav, weights,
    disclosures, factors, fundFactors, valuations, classifications, book, prices, trades, stockWeights,
    fundamentals, fundamentalField,
    style, styleRisk]);
}
